import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

import useAppSelector from '~hooks/useAppSelector';
import {
  fetchNewsfeedNews,
  fetchNewsfeedPreferences,
  NewsfeedApiKeysEnum,
  postNewsfeedPreferences,
} from '~services/api/calls/newsfeed';
import { createNews } from '~services/api/factories/createNews';
import { type FetchPreferences } from '~services/api/validators/Newsfeed';
import { selectAddonsIsNewsfeedEnabled, selectUserIsLoggedIn } from '~store/selectors';

async function fetchAndSanitizeNews() {
  const fetchedNews = await fetchNewsfeedNews();
  const newsList = createNews(fetchedNews);
  return newsList;
}

export function useNewsQuery(enabled = false) {
  const [dataNews, setDataNews] = useState<NewsItem[]>([]);
  const [isFetchedNews, setIsFetchedNews] = useState(false);
  const [isErrorNews, setIsErrorNews] = useState(false);

  const { data, isFetched, isError } = useQuery({
    queryKey: [NewsfeedApiKeysEnum.FetchNews],
    queryFn: fetchAndSanitizeNews,
    enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    placeholderData: [],
  });

  useDeepCompareEffect(() => {
    setDataNews(data || []);
    setIsFetchedNews(isFetched);
    setIsErrorNews(isError);
  }, [data, isFetched, isError]);

  return { dataNews, isFetchedNews, isErrorNews };
}

export function usePreferencesQuery(enabled = false) {
  const [dataPreferences, setDataPreferences] = useState<FetchPreferences>({ topics: [] });
  const [isFetchedPreferences, setIsFetchedPreferences] = useState(false);
  const [isErrorPreferences, setIsErrorPreferences] = useState(false);

  const { data, isFetched, isError } = useQuery({
    queryKey: ['fetchPreferences'],
    queryFn: async () => await fetchNewsfeedPreferences(),
    enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    placeholderData: {
      topics: [],
    },
  });

  useDeepCompareEffect(() => {
    setDataPreferences({ topics: data.topics || [] });
    setIsFetchedPreferences(isFetched);
    setIsErrorPreferences(isError);
  }, [data, isFetched, isError]);

  return { dataPreferences, isFetchedPreferences, isErrorPreferences };
}

export function useNewsfeed() {
  const isEnabled = useAppSelector(selectAddonsIsNewsfeedEnabled);
  const isUserLoggedIn = useAppSelector(selectUserIsLoggedIn);
  const { dataNews } = useNewsQuery(isEnabled && isUserLoggedIn);
  const { dataPreferences } = usePreferencesQuery(isEnabled && isUserLoggedIn);
  return useMemo(() => ({ newsList: dataNews, preferences: dataPreferences }), [dataNews, dataPreferences]);
}

export function useMutatePreferences() {
  const queryClient = useQueryClient();
  const isNewsfeedEnabled = useAppSelector(selectAddonsIsNewsfeedEnabled);
  const { dataPreferences } = usePreferencesQuery(isNewsfeedEnabled);
  const [selectedTopicIds, setSelectedTopicIds] = useState<TopicId[]>([]);
  const [hasTopicsSelected, setHasTopicsSelected] = useState(false);

  const { mutateAsync } = useMutation({
    mutationFn: postNewsfeedPreferences,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [NewsfeedApiKeysEnum.FetchPreferences] });
      void queryClient.invalidateQueries({ queryKey: [NewsfeedApiKeysEnum.FetchNews] });
    },
  });

  useDeepCompareEffect(() => {
    const topicIds = dataPreferences?.topics || [];
    setSelectedTopicIds(topicIds);
    setHasTopicsSelected(topicIds.length > 0);
  }, [dataPreferences]);

  const handleSavePreferences = useCallback(async () => {
    const pref = {
      topics: selectedTopicIds,
    };
    const res = await mutateAsync(pref);
    return res;
  }, [mutateAsync, selectedTopicIds]);

  const handleResetSelectedTopics = useCallback(
    async function resetTopics() {
      setSelectedTopicIds([]);
      const pref = {
        topics: [],
      };
      const res = await mutateAsync(pref);
      return res;
    },
    [mutateAsync],
  );

  return useMemo(
    () => ({
      topicIds: selectedTopicIds,
      hasTopicsSelected,
      updateTopicIds: setSelectedTopicIds,
      save: handleSavePreferences,
      resetTopics: handleResetSelectedTopics,
    }),
    [handleSavePreferences, selectedTopicIds, hasTopicsSelected, handleResetSelectedTopics],
  );
}
